export type PlayerSourceType = "live" | "vod";

export type PlayerMode = "hls" | "mpegts" | "native";

export interface PlayerSource {
  url: string;
  type: PlayerSourceType;
  /** Playback engine hint. When omitted the player sniffs the URL (`.m3u8` → hls, otherwise mpegts, `.mp4/.webm` → native). */
  format?: PlayerMode;
  /**
   * True when the server serves this stream through the FFmpeg transcode pipe:
   * a live-shaped MPEG-TS feed (no content-length, no range support) rather
   * than a finite seekable VOD response. When `durationSeconds` is also
   * provided, the player uses the finite VOD loader with a filesize estimate
   * so the buffer fills in chunks from the download (smooth, no stutter).
   * Without `durationSeconds`, the player falls back to the live pipe.
   */
  transcoded?: boolean;
  /**
   * Known length in SECONDS, supplied by the caller when the transport cannot
   * report one. A transcoded VOD is a live-shaped pipe, so `video.duration` is
   * null for it; without this the seek bar has no scale. Approximate (TMDB
   * runtime) — good enough for a progress indicator, not for frame accuracy.
   */
  durationSeconds?: number;
  /**
   * True when `durationSeconds` is EXACT and must win over whatever length the
   * transport measures. Catch-up is the case: the panel serves precisely the
   * minutes asked for (measured 2026-09-18 — 5, 30 and 60-minute requests
   * returned 1×, 6.2× and 12.2× the bytes), while ExoPlayer, measuring an
   * unindexed MPEG-TS from its timestamps, reported 24.5 hours for a one-hour
   * replay whose archive had a timestamp jump in it.
   */
  durationIsExact?: boolean;
  altUrls?: string[];
  title?: string;
  artist?: string;
  album?: string;
  artworkUrl?: string;
}

export type PlayerStatus =
  | "idle"
  | "loading"
  | "playing"
  | "paused"
  | "buffering"
  | "retrying"
  | "error"
  | "ended";

export type PlayerEvent =
  | "statechange"
  | "playing"
  | "paused"
  | "buffering"
  | "retrying"
  | "error"
  | "ended"
  | "timeupdate"
  | "loadedmetadata"
  | "levelSwitched";

export type PlayerErrorCode =
  | "unsupported"
  | "manifest"
  | "network"
  | "media"
  | "autoplay"
  /**
   * The stream never produced a first frame inside the player's deadline.
   *
   * Deliberately NOT `exhausted`: that code is the app's free-tier meter ("free
   * viewing time is up"), and the two used to share it — so a channel that never
   * started was labelled to the viewer as a spent allowance. They are different
   * things and now read differently.
   */
  | "timeout"
  /**
   * Every retry of every address the channel has (its own, its alternatives, a
   * mirror) failed. Only then does the viewer see an error (owner, 2026-10-19:
   * "don't answer with an error too early if the provider is still trying to
   * send streams ... only report real errors, not something like switching
   * feeds"). Until 2026-10-19 this ended as `exhausted`, the free-tier meter's
   * code, so the guide preview read "Free viewing time is used up".
   */
  | "unavailable"
  /** The free-tier meter stopped playback (apps/web playerStore). Nothing else. */
  | "exhausted";

export interface PlayerError {
  code: PlayerErrorCode;
  message: string;
  recoverable: boolean;
  sourceUrl?: string;
  /** The provider's HTTP answer on the last attempt, when the engine knows it. */
  httpStatus?: number;
}

export interface PlayerState {
  status: PlayerStatus;
  source?: PlayerSource;
  /** Active playback engine for the current source (hls.js, mpegts.js, or native element playback). */
  mode?: PlayerMode;
  currentTime: number;
  duration: number;
  bufferedAhead: number;
  volume: number;
  muted: boolean;
  attempt: number;
  error?: PlayerError;
}

export type PlayerEventPayload =
  | PlayerState
  | PlayerError
  | { currentTime: number; duration: number; bufferedAhead: number }
  | { level: number; bitrate?: number };

/**
 * How far back a live stream can currently be rewound, and where the playhead
 * sits inside that window.
 *
 * Reports what the browser has ACTUALLY retained rather than the configured
 * window: the back buffer lives in the MediaSource SourceBuffer, and the
 * browser evicts from it under memory pressure whatever we asked for. A UI that
 * trusted the configured number would offer a rewind that silently fails.
 */
export interface TimeshiftWindow {
  /** True only for live sources with something retained behind the playhead. */
  available: boolean;
  /** Seconds of retained content behind the playhead, right now. */
  behindSeconds: number;
  /** Seconds the playhead currently sits behind the live edge. 0 = at live. */
  offsetSeconds: number;
  /**
   * Why live TV is playing without a timeshift buffer, when the engine keeps one
   * (the APK): "no-space" (the drive is too full), "unsupported" (the channel is
   * not HLS) or "failed". Absent while the buffer runs or when there is none to keep.
   */
  offReason?: string;
}

/**
 * How the picture fills its box, the option every set-top box calls "aspect
 * ratio": FIT letterboxes (the stream's own shape, nothing lost), FILL zooms
 * until no bars remain (the edges are cropped), STRETCH distorts to the box.
 * ZOOM enlarges the picture by a third in full screen, cropping the black bars
 * a channel sends INSIDE a widescreen picture (a 2.39:1 film in a 16:9 stream),
 * which FILL cannot touch: the picture already has the screen's shape (owner,
 * 2026-10-06). Outside full screen it shows as FIT.
 */
export type AspectMode = "fit" | "fill" | "stretch" | "zoom";

/** How much ZOOM enlarges the picture: a 2.39:1 film's bars in a 16:9 frame are cropped. */
export const ZOOM_FACTOR = 4 / 3;

/**
 * One selectable audio or subtitle track of the stream now playing. Ids are
 * opaque and engine-specific — read them from {@link Player.tracks} and hand
 * them back to {@link Player.selectTrack} unchanged.
 */
export interface MediaTrack {
  id: string;
  kind: "audio" | "text";
  /** Human label: the stream's own name, else its language, else a number. */
  label: string;
  language?: string;
  selected: boolean;
}

export interface Player {
  load(source: PlayerSource): Promise<void>;
  play(): Promise<void>;
  pause(): void;
  seek(seconds: number): void;
  /**
   * What can currently be rewound on a live stream. Poll this to drive the
   * rewind UI; it changes as the buffer fills and as the browser evicts.
   */
  timeshift(): TimeshiftWindow;
  /**
   * Move relative to the LIVE EDGE: -30 goes thirty seconds back, 0 returns to
   * live. Expressed against the edge rather than absolute time because a live
   * stream's timeline start moves as segments are evicted, so an absolute
   * position means something different a minute later. Clamped to what is
   * retained. No-op on non-live sources.
   */
  seekFromLiveEdge(offsetSeconds: number): void;
  setVolume(volume: number): void;
  /** True mute: sets the element's muted flag WITHOUT touching volume, so
   * unmuting restores the previous level. `volume: 0` is not the same as
   * muted — it loses the last volume. */
  setMuted(muted: boolean): void;
  /**
   * Attach a known length (seconds) to the CURRENT source after it started
   * playing. Used when the caller learns the real runtime asynchronously
   * (e.g. a TMDB lookup after a Search/Home click) — the seek bar and
   * progress saving read `durationSeconds`, and a transcoded pipe cannot
   * report its own length. No-op when nothing is loaded.
   */
  updateDuration(seconds: number): void;
  /** Stop the current source and release its upstream connection, keeping the
   *  player reusable. pause() leaves the request open and the server's ffmpeg
   *  child holding a provider session; stop() closes it and cancels pending
   *  reconnects. */
  stop(): void;
  /**
   * The audio and subtitle tracks the CURRENT stream offers, and which are on.
   * Optional: an engine that cannot enumerate them (the cast adapter, test
   * doubles) simply omits it and the UI hides the choice.
   */
  tracks?(): Promise<MediaTrack[]>;
  /** Turn on one track of a kind, or `null` for none (subtitles off). */
  selectTrack?(kind: "audio" | "text", id: string | null): Promise<void> | void;
  /** Playback speed, 1 = normal. Live streams ignore it. */
  setPlaybackRate?(rate: number): void;
  /** How the picture fills its box (see {@link AspectMode}). */
  setAspectMode?(mode: AspectMode): void;
  getState(): PlayerState;
  on(event: PlayerEvent, callback: (payload: PlayerEventPayload) => void): () => void;
  destroy(): void;
}
